import type { ItemResponse } from '@palaccount/contracts';
import * as React from 'react';

import type { TypeaheadOption } from '@/components/ui/typeahead';
import { i18n } from '@/i18n';
import { localizedName } from '@/lib/localized-name';
import { useDebounced, usePickerState, type PickerState } from '@/lib/typeahead';

import { useItemSearch, useItemsHeld, type ItemListFilters } from './api';

/**
 * THE ITEM PICKER (GEN-50), used by every document editor that has a line
 * grid — the ~15 that used to load 50,000 rows into a `<select>` each.
 *
 * One hook rather than fifteen copies, because the sequence is the same
 * everywhere and each step has a way of being wrong that a reader would blame
 * on the screen: the search is debounced, the ids the FORM already holds are
 * resolved separately, and the two sets are merged so that `byId` answers for
 * an item whether it was typed for or saved last year.
 *
 * `heldIds` is the ids of the lines being edited. It is a plain array from the
 * caller's own state — passing the lines instead would make this hook know
 * what a line is, and a delivery-note line and a recipe component are not the
 * same shape.
 */
export function useItemPicker(
  heldIds: readonly (string | null | undefined)[],
  filters: ItemListFilters = {},
): PickerState<ItemResponse> {
  const [query, setQuery] = React.useState('');
  const search = useDebounced(query);
  const ids = React.useMemo(
    () => heldIds.filter((id): id is string => typeof id === 'string' && id.length > 0),
    [heldIds],
  );

  const matches = useItemSearch(search, filters);
  const held = useItemsHeld(ids);

  return usePickerState<ItemResponse>({
    query,
    setQuery,
    search,
    matches: matches.data,
    held: held.data,
    toOption: itemOption,
    loading: matches.isFetching,
  });
}

/**
 * MD-03 §4a's own two columns, in the order the screen prints them: the name
 * a reader scans, and the code they were given on the phone. The search box
 * behind this matches BOTH (plus the category), so a row can appear for a
 * reason the label does not show — which is why the code rides along as the
 * hint rather than being left to the tooltip.
 */
export function itemOption(item: ItemResponse): TypeaheadOption {
  return {
    id: item.id,
    // S5: the Arabic twin on an Arabic screen; the code rides as the hint.
    label: localizedName(item, i18n.language),
    hint: item.code,
    inactive: !item.isActive,
  };
}
